import { WizardPageType } from '@rover/react-lib/src/pages/search/SearchPage/WizardPageType';
import BusinessType, {
  ALL_BUSINESS_TYPES,
} from '@rover/shared/js/constants/businessType.constants';
import {
  CAT_DOG_PREFERENCES_SUPPORTED_SERVICES,
  SHOW_SPACES_REQUIRED_WITH_PETS_SERVICES,
  SPACES_REQUIRED_SUPPORTED_SERVICES,
} from '@rover/shared/js/constants/searchPage.constants';
import { DAYTIME_SERVICES, SERVICE_TYPE_CHOICES } from '@rover/shared/js/constants/service';
import { QueryParams } from '@rover/shared/js/search/utilities/filtersToQuery.types';
import {
  type CategorizedFilterOption,
  type SearchFilters,
  type SearchFiltersOptions,
  type ServiceType,
} from '@rover/types';
import { PetType } from '@rover/types/src/Pet';
import { ServiceFrequencyEnum } from '@rover/types/src/ServiceFrequency';

type ShowControlsOptions = {
  hasPets?: boolean;
  isCatOnly?: boolean;
  pageType?: WizardPageType;
  serviceOptions?: SearchFiltersOptions;
  serviceType?: Partial<ServiceType>;
  serviceTypeSlug?: string;
  isInDaycareGingrFacilitiesExperiment?: boolean;
  isInBoardingGingrFacilitiesExperiment?: boolean;
  isDaycareFilterOutsideMarketsInExperiment?: boolean;
  showStarSitterFilter?: boolean;
  isPriceTransparencyFull?: boolean;
  isIncludingFees?: boolean;
  searchFilters?: SearchFilters;
};

export type ShowControls = {
  showCatPreferences: boolean;
  showDateRange: boolean;
  showDaycareType: boolean;
  showBoardingType: boolean;
  showDaytimeAvailability: boolean;
  showDaytimeServiceScheduler: boolean;
  showDogPreferences: boolean;
  showFulltimeAvailability: boolean;
  showPets: boolean;
  showPetSize: boolean;
  showPetType: boolean;
  showPrice: boolean;
  showPuppyPreferences: boolean;
  showSpacesRequired: boolean;
  showStarSitterOnlyFilter: boolean;
  lockPriceToggle: boolean;
};

const shouldShowCatPreferences = (
  hasPets: boolean,
  isCatOnly: boolean,
  serviceTypeSlug: string,
  noCats?: CategorizedFilterOption
): boolean => {
  if (hasPets || isCatOnly) {
    return false;
  }

  return (
    !!noCats &&
    // @ts-expect-error TS is dumb and doesn't let us check if `serviceTypeSlug` is one of the specific `ServiceType` we care about
    CAT_DOG_PREFERENCES_SUPPORTED_SERVICES.has(serviceTypeSlug)
  );
};

export const shouldShowDateRange = ({
  serviceType,
}: {
  serviceType?: Partial<ServiceType>;
}): boolean => !!serviceType?.supportsStartEndDate;

const isAvailabilitySet = (filter?: boolean | string, option?: CategorizedFilterOption): boolean =>
  filter !== undefined || !!option;

export const shouldShowDaytimeAvailability = (
  serviceTypeSlug: string,
  filters: Partial<SearchFilters | QueryParams> = {},
  serviceTypeOptions: SearchFiltersOptions = {}
): boolean => {
  /**
   * We should respect the morning, midday, or evening availability if they are
   * present in the filters, otherwise fall back on options.
   *
   * Availability filters may be provided as booleans or strings.
   */
  const morningAvailability = isAvailabilitySet(
    filters?.morningAvailability,
    serviceTypeOptions.morningAvailability
  );
  const middayAvailability = isAvailabilitySet(
    filters?.middayAvailability,
    serviceTypeOptions.middayAvailability
  );
  const eveningAvailability = isAvailabilitySet(
    filters?.eveningAvailability,
    serviceTypeOptions.eveningAvailability
  );

  const isServiceAllowed =
    serviceTypeSlug === SERVICE_TYPE_CHOICES.walking ||
    serviceTypeSlug === SERVICE_TYPE_CHOICES.dropIn;

  return isServiceAllowed && morningAvailability && middayAvailability && eveningAvailability;
};

export const shouldShowDaytimeServiceScheduler = (
  supportsRepeatBookings: boolean = false,
  serviceTypeSlug: string = ''
): boolean => supportsRepeatBookings && serviceTypeSlug !== SERVICE_TYPE_CHOICES.assignmentGrooming;

export const shouldShowFulltimeAvailability = (serviceTypeSlug: string): boolean =>
  serviceTypeSlug === SERVICE_TYPE_CHOICES.dayCare;

const shouldShowDogPreferences = (
  hasPets: boolean,
  isCatOnly: boolean,
  serviceTypeSlug: string,
  personDoesNotHaveDogs?: CategorizedFilterOption
): boolean => {
  if (hasPets || isCatOnly) {
    return false;
  }

  return (
    !!personDoesNotHaveDogs &&
    // @ts-expect-error TS is dumb and doesn't let us check if `serviceTypeSlug` is one of the specific `ServiceType` we care about
    CAT_DOG_PREFERENCES_SUPPORTED_SERVICES.has(serviceTypeSlug)
  );
};

const shouldShowPuppyPreferences = (
  hasPets: boolean,
  isCatOnly: boolean,
  puppy?: CategorizedFilterOption
): boolean => {
  if (hasPets || isCatOnly) {
    return false;
  }
  return !!puppy;
};

const shouldShowSpacesRequired = (
  hasPets: boolean,
  pageType: WizardPageType,
  serviceTypeSlug: string
): boolean => {
  if (pageType === WizardPageType.MODAL) {
    return false;
  }

  if (hasPets) {
    // @ts-expect-error TS is dumb and doesn't let us check if `serviceTypeSlug` is one of the specific `ServiceType` we care about
    return SHOW_SPACES_REQUIRED_WITH_PETS_SERVICES.has(serviceTypeSlug);
  }
  // @ts-expect-error TS is dumb and doesn't let us check if `serviceTypeSlug` is one of the specific `ServiceType` we care about
  return SPACES_REQUIRED_SUPPORTED_SERVICES.has(serviceTypeSlug);
};

const shouldShowDaycareType = (
  hasCats: boolean,
  serviceTypeSlug: string,
  isInDaycareGingrFacilitiesExperiment = false,
  isDaycareFilterOutsideMarketsInExperiment = false
): boolean =>
  !hasCats &&
  serviceTypeSlug === SERVICE_TYPE_CHOICES.dayCare &&
  (isInDaycareGingrFacilitiesExperiment || isDaycareFilterOutsideMarketsInExperiment);

const shouldShowBoardingType = (
  hasCats: boolean,
  serviceTypeSlug: string,
  isInBoardingGingrFacilitiesExperiment = false
): boolean =>
  !hasCats &&
  serviceTypeSlug === SERVICE_TYPE_CHOICES.boarding &&
  isInBoardingGingrFacilitiesExperiment;

export const shouldShowPrice = (
  maxPrice?: CategorizedFilterOption,
  minPrice?: CategorizedFilterOption,
  isPriceTransparencyFull?: boolean,
  isIncludingFees?: boolean
): boolean => {
  if (!!minPrice && !!maxPrice) {
    return !isPriceTransparencyFull || Boolean(isIncludingFees);
  }

  return false;
};

export const lockPriceToggle = (searchFilters?: Partial<SearchFilters>): boolean => {
  const isDaytimeService =
    searchFilters?.serviceType && DAYTIME_SERVICES.includes(searchFilters.serviceType);
  const includesDaycareCenter =
    (searchFilters?.serviceType === SERVICE_TYPE_CHOICES.boarding &&
      (searchFilters?.businessType === BusinessType.FACILITY ||
        searchFilters?.businessType === ALL_BUSINESS_TYPES)) ||
    (searchFilters?.serviceType === SERVICE_TYPE_CHOICES.dayCare &&
      searchFilters?.atDaycareFacility);
  return (
    ((isDaytimeService && searchFilters?.frequency === ServiceFrequencyEnum.REPEAT) ||
      includesDaycareCenter) ??
    false
  );
};

export default ({
  hasPets = true,
  isCatOnly = false,
  pageType = WizardPageType.DEFAULT,
  serviceOptions = {},
  serviceType,
  serviceTypeSlug = '',
  isInDaycareGingrFacilitiesExperiment,
  isInBoardingGingrFacilitiesExperiment,
  isDaycareFilterOutsideMarketsInExperiment,
  showStarSitterFilter,
  isPriceTransparencyFull,
  isIncludingFees,
  searchFilters,
}: ShowControlsOptions): ShowControls => {
  const { maxprice, minprice, noCats, personDoesNotHaveDogs, pets, puppy } = serviceOptions;
  const hasCats = (searchFilters?.petType || []).includes(PetType.CAT);
  const serviceSlug = serviceTypeSlug || serviceType?.slug || '';

  return {
    showCatPreferences: shouldShowCatPreferences(hasPets, isCatOnly, serviceSlug, noCats),
    showDateRange: shouldShowDateRange({ serviceType }),
    showDaycareType: shouldShowDaycareType(
      hasCats,
      serviceSlug,
      isInDaycareGingrFacilitiesExperiment,
      isDaycareFilterOutsideMarketsInExperiment
    ),
    showBoardingType: shouldShowBoardingType(
      hasCats,
      serviceSlug,
      isInBoardingGingrFacilitiesExperiment
    ),
    showDaytimeAvailability: shouldShowDaytimeAvailability(serviceSlug, {}, serviceOptions),
    showDaytimeServiceScheduler: shouldShowDaytimeServiceScheduler(
      serviceType?.supportsRepeatBookings,
      serviceSlug
    ),
    showDogPreferences: shouldShowDogPreferences(
      hasPets,
      isCatOnly,
      serviceSlug,
      personDoesNotHaveDogs
    ),
    showFulltimeAvailability: shouldShowFulltimeAvailability(serviceSlug),
    showPets: !!pets?.choices?.length,
    showPetSize: !(hasPets || isCatOnly),
    showPetType: !hasPets,
    showPrice: shouldShowPrice(maxprice, minprice, isPriceTransparencyFull, isIncludingFees),
    showPuppyPreferences: shouldShowPuppyPreferences(hasPets, isCatOnly, puppy),
    showSpacesRequired: shouldShowSpacesRequired(hasPets, pageType, serviceSlug),
    showStarSitterOnlyFilter: !!showStarSitterFilter,
    lockPriceToggle: lockPriceToggle(searchFilters),
  };
};
